import { useEffect, useState } from 'react';
import { useSearchResultLang } from './useSearchResultLang';

import { MIN_LENGTH_SEARCH_QUERY } from 'features/menu-page/domain';

import type {
  FullMenuCatalog,
  IMenuCustomization,
  IMenuProduct,
} from 'features/menu-page/domain';

const INIT_SEARCH_RESULTS = {
  products: [],
  customizations: [],
} as {
  products: IMenuProduct[];
  customizations: IMenuCustomization[];
};

export function useSearchResult(props: Props) {
  const { searchQuery, catalog } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(INIT_SEARCH_RESULTS);

  const notResults =
    searchResults.products.length === 0 &&
    searchResults.customizations.length === 0;

  const lang = useSearchResultLang({ notResults });

  const filterProductsAndCustomizationsBySearchQuery = () => {
    const queryToLowerCase = searchQuery.toLowerCase();
    const _products = Array.from(
      new Map(
        catalog.products
          .filter(
            p =>
              p.name.toLowerCase().includes(queryToLowerCase) ||
              p.description.toLowerCase().includes(queryToLowerCase),
          )
          .map(p => [p.id, p]),
      ).values(),
    );
    const _customs = [
      ...new Map(
        catalog.customizationGroups
          .reduce(
            (acc, cv) => [...acc, ...cv.items],
            [] as IMenuCustomization[],
          )
          .filter(c => c.description.toLowerCase().includes(queryToLowerCase))
          .map(c => [c.id, c]),
      ).values(),
    ];
    setSearchResults({ products: _products, customizations: _customs });
    setIsLoading(false);
  };

  useEffect(() => {
    let delayDebounceFn: NodeJS.Timeout;
    if (searchQuery.length >= MIN_LENGTH_SEARCH_QUERY) {
      setIsLoading(true);
      delayDebounceFn = setTimeout(() => {
        filterProductsAndCustomizationsBySearchQuery();
      }, 1_000);
    } else {
      setSearchResults(INIT_SEARCH_RESULTS);
    }
    return () => {
      setIsLoading(false);
      clearTimeout(delayDebounceFn);
    };
  }, [searchQuery, catalog]);

  return {
    lang,
    isLoading,
    products: searchResults.products,
    customizations: searchResults.customizations,
  };
}

interface Props {
  searchQuery: string;
  catalog: FullMenuCatalog;
}
